import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useComparison } from '../context/ComparisonContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../translations/translations';

// A metric row: label, value extractor, formatter, and which direction "wins".
// best: 'high' | 'low' | null (null = no winner highlighting)
const buildRows = (language) => [
  { label: `${t(language, 'card.kilometers')} `, key: 'rangeKm', fmt: (c) => `${c.rangeKm} km`, best: 'high' },
  { label: t(language, 'card.maxCharging'), key: 'maxChargingSpeed', fmt: (c) => `${c.maxChargingSpeed} kW`, best: 'high' },
  { label: t(language, 'card.battery'), key: 'batterySize', fmt: (c) => `${c.batterySize} kWh${c.batteryChemistry ? ` (${c.batteryChemistry})` : ''}`, best: null },
  { label: t(language, 'card.wltpRange'), key: 'wltpRange', fmt: (c) => `${c.wltpRange} km`, best: 'high' },
  { label: t(language, 'card.power'), key: 'power', fmt: (c) => `${c.power} hp`, best: 'high' },
  { label: t(language, 'card.weight'), key: 'weight', fmt: (c) => `${c.weight} kg`, best: 'low' },
  { label: t(language, 'card.testTemp'), key: 'temperature', fmt: (c) => `${c.temperature}°C`, best: null },
  { label: t(language, 'card.drive'), key: 'drivetrain', fmt: (c) => c.drivetrain, best: null },
  { label: t(language, 'card.tires'), key: 'tires', fmt: (c) => c.tires || '–', best: null },
  { label: t(language, 'card.tested'), key: 'testDate', fmt: (c) => c.testDate || '–', best: null },
];

const ComparisonView = () => {
  const { language } = useLanguage();
  const { darkMode } = useTheme();
  const { comparedCars, showComparison, toggleComparison, removeFromComparison, clearComparison } = useComparison();

  if (!showComparison || comparedCars.length === 0) {
    return null;
  }

  const rows = buildRows(language);

  const winnerIds = (row) => {
    if (!row.best || comparedCars.length < 2) return [];
    const getVal = row.value || ((c) => c[row.key]);
    const vals = comparedCars.map((c) => getVal(c)).filter((v) => v !== null && v !== undefined);
    if (vals.length < 2 || new Set(vals).size === 1) return [];
    const target = row.best === 'high' ? Math.max(...vals) : Math.min(...vals);
    return comparedCars.filter((c) => getVal(c) === target).map((c) => c.id);
  };

  const panelBox = darkMode
    ? 'bg-slate-900 border border-teal-500 border-opacity-30 text-gray-100'
    : 'bg-white border border-stone-200 text-stone-900';
  const labelText = darkMode ? 'text-gray-400' : 'text-stone-500';
  const rowBorder = darkMode ? 'border-slate-700' : 'border-stone-100';

  return (
    <>
      {/* Backdrop blur */}
      <div
        className="fixed inset-0 bg-slate-900 bg-opacity-80 backdrop-blur-sm z-[100]"
        onClick={toggleComparison}
      ></div>

      {/* Comparison panel */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center overflow-auto p-4 sm:p-8" onClick={toggleComparison}>
        <div
          className={`relative w-full max-w-5xl rounded-2xl shadow-2xl max-h-full overflow-auto ${panelBox}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 sticky top-0 z-10 bg-inherit">
            <h2 className="text-xl font-bold">{t(language, 'card.comparisonTitle')}</h2>
            <button
              onClick={toggleComparison}
              aria-label={t(language, 'card.closeComparison')}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="overflow-x-auto px-5 pb-5">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left align-bottom pb-3 pr-4"></th>
                  {comparedCars.map((car) => (
                    <th key={car.id} className="text-left align-bottom pb-3 pr-4 min-w-[10rem]">
                      <div className={`font-bold text-base ${darkMode ? 'text-teal-400' : 'text-teal-600'}`}>{car.make}</div>
                      <div className="font-semibold">{car.model}</div>
                      <div className={`text-xs font-normal ${labelText}`}>{car.year}</div>
                      <button
                        onClick={() => removeFromComparison(car.id)}
                        className={`mt-2 text-xs font-semibold px-2 py-1 rounded transition-colors ${
                          darkMode ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                        }`}
                      >
                        {t(language, 'card.removeFromComparison')} ✕
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const winners = winnerIds(row);
                  return (
                    <tr key={row.key} className={`border-t ${rowBorder}`}>
                      <td className={`py-2.5 pr-4 text-xs uppercase tracking-wide font-semibold whitespace-nowrap ${labelText}`}>
                        {row.label}
                      </td>
                      {comparedCars.map((car) => (
                        <td
                          key={car.id}
                          className={`py-2.5 pr-4 ${
                            winners.includes(car.id)
                              ? `font-bold ${darkMode ? 'text-teal-400' : 'text-teal-600'}`
                              : ''
                          }`}
                        >
                          {row.fmt(car)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                <tr className={`border-t ${rowBorder}`}>
                  <td className="py-3 pr-4"></td>
                  {comparedCars.map((car) => (
                    <td key={car.id} className="py-3 pr-4">
                      {car.youtubeUrl && (
                        <a
                          href={car.youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-red-500 hover:text-red-400 font-semibold"
                        >
                          YouTube →
                        </a>
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          <div className={`px-5 pb-5 text-right`}>
            <button
              onClick={clearComparison}
              className={`text-sm font-semibold px-4 py-2 rounded-lg transition-colors ${
                darkMode ? 'bg-slate-800 text-gray-300 hover:bg-slate-700' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              {t(language, 'card.clearAll')} ({comparedCars.length})
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ComparisonView;
